/**
 * Form de Cliente (cria + edita) — usado dentro de SlideOverPanel.
 *
 * Campos básicos à vista (tipo, nome, documento, contato, dados do tipo de
 * pessoa, endereços). Os enderecos sao titulados (ADR-0071): o 1o e o
 * principal, um deles pode ser o de cobranca (sem marca, vale o principal) e
 * o CEP preenche logradouro/bairro/cidade/UF. A lista vai inteira no
 * POST/PATCH em `enderecos`.
 *
 * Em seções recolhíveis ficam o que a ficha mostrava e nenhuma tela editava
 * (CRM-36): telefone 2, parceiro indicador, marcadores e limite de crédito.
 *
 * Só os campos do tipo escolhido vão no envio (CRM-44): trocar PF -> PJ não
 * leva RG/nascimento, e PJ -> PF não leva nome fantasia/IE. O documento é
 * validado como CPF (PF) ou CNPJ (PJ) com dígito verificador (CRM-16), com a
 * mesma mensagem do backend, e sai só com o número.
 *
 * Props:
 *   - cliente: payload do ClienteSerializer (edicao) ou null (criacao).
 *   - onSubmit(dados): Promise. A rejeicao e o erro do `request`, com o corpo
 *     do 400 em `err.payload`. O form mostra TODO erro (campo, endereço e
 *     mensagem geral): o pai chama a API com `silentError`.
 *   - onCancel, loading.
 *   - buscarCep?(cep8digitos): busca de CEP injetada pelo pai — em geral
 *     `(cep) => buscarCep(request, cep)` de services/enderecos.
 *   - carregarParceiros?(): Promise da lista (ou página) de parceiros da
 *     unidade — em geral `listarRepresentantes`. Passe uma função ESTÁVEL
 *     (useCallback): trocar a função recarrega a lista.
 */
import React, { useEffect, useMemo, useState } from "react";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";

import AvisoContatoExistente, {
  useContatoExistente,
} from "../../components/AvisoContatoExistente";
import EnderecosEditor from "../../components/EnderecosEditor";
import TagsInput from "../../components/TagsInput";
import { formatarDinheiro, parseDinheiro } from "../../utils/dinheiro";
import {
  erroDoDocumento,
  mascaraDocumento,
  normalizarDocumento,
  PESSOA_FISICA,
  PESSOA_JURIDICA,
} from "../../utils/documento";
import { errosDaRespostaNosCartoes, prepararEnderecos, validarCartoes } from "../../utils/endereco";
import { CAMPO_GERAL, errosPorCampo } from "../../utils/errosApi";
import { mascaraTelefone } from "../../utils/leadFormat";
import {
  ErrorMsg,
  Field,
  FieldGroup,
  FieldGroupTitle,
  Form,
  FormFooter,
  FormGrid,
  FormSelect,
  InlineHint,
  Input,
  PrimaryButton,
  SecondaryButton,
  Textarea,
} from "./crmStyles";

const TIPOS = [
  { value: PESSOA_FISICA, label: "Pessoa física (PF)" },
  { value: PESSOA_JURIDICA, label: "Pessoa jurídica (PJ)" },
];

const PERFIS = [
  { value: "", label: "—" },
  { value: "Residencial", label: "Residencial" },
  { value: "Corporativo", label: "Corporativo" },
  { value: "Construtora", label: "Construtora" },
  { value: "Outro", label: "Outro" },
];

// Mesmos conjuntos de `CAMPOS_SO_DE_PESSOA_*` do ClienteSerializer.
const CAMPOS_SO_DE_PESSOA_FISICA = ["rg", "data_nascimento", "estado_civil", "profissao"];
const CAMPOS_SO_DE_PESSOA_JURIDICA = ["nome_fantasia", "inscricao_estadual"];

// Campos que o form mostra: erro de outra chave vai para o aviso geral, para
// nunca sumir sem aparecer em lugar nenhum.
const CAMPOS_NA_TELA = [
  "tipo", "perfil", "nome", "nome_fantasia", "documento", "inscricao_estadual",
  "telefone", "email", "rg", "data_nascimento", "estado_civil", "profissao",
  "telefone_secundario", "representante", "marcadores", "limite_credito", "observacoes",
];

// Chaves do corpo de erro que não são campo (formato de endereço recusado).
const CHAVES_QUE_NAO_SAO_CAMPO = ["enderecos", "detalhe", "detail", "erro", "erros", "mensagem", "codigo"];

const SECAO_CONTATO = "contato";
const SECAO_COMERCIAL = "comercial";
const CAMPOS_DA_SECAO = {
  [SECAO_CONTATO]: ["telefone_secundario"],
  [SECAO_COMERCIAL]: ["representante", "marcadores", "limite_credito"],
};

const MSG_NOME_OBRIGATORIO = "Nome é obrigatório.";
// Igual a `ClienteSerializer.validate_limite_credito`.
export const MSG_LIMITE_NEGATIVO = "O limite de crédito não pode ser negativo.";
export const MSG_LIMITE_ILEGIVEL = "Informe o limite em reais, ex.: 15.000,00.";

function textoDoLimite(limite) {
  if (limite === null || limite === undefined || limite === "") return "";
  const numero = Number(limite);
  return Number.isFinite(numero) ? formatarDinheiro(numero) : "";
}

/** Texto do campo -> `{valor: "15000.00" | null, erro}`. Vazio = sem limite. */
function lerLimite(texto) {
  if (!String(texto ?? "").trim()) return { valor: null, erro: "" };
  const numero = parseDinheiro(texto);
  if (numero === null) return { valor: null, erro: MSG_LIMITE_ILEGIVEL };
  if (numero < 0) return { valor: null, erro: MSG_LIMITE_NEGATIVO };
  return { valor: numero.toFixed(2), erro: "" };
}

function valoresIniciais(cliente) {
  const tipo = cliente?.tipo || PESSOA_FISICA;
  return {
    tipo,
    nome: cliente?.nome || "",
    nome_fantasia: cliente?.nome_fantasia || "",
    inscricao_estadual: cliente?.inscricao_estadual || "",
    documento: mascaraDocumento(cliente?.documento || "", tipo),
    // Pessoa fisica (exibidos so quando tipo=PF)
    rg: cliente?.rg || "",
    data_nascimento: cliente?.data_nascimento || "",
    estado_civil: cliente?.estado_civil || "",
    profissao: cliente?.profissao || "",
    email: cliente?.email || "",
    telefone: cliente?.telefone || "",
    telefone_secundario: cliente?.telefone_secundario || "",
    perfil: cliente?.perfil || "",
    representante: cliente?.representante || "",
    marcadores: Array.isArray(cliente?.marcadores) ? cliente.marcadores : [],
    limite_credito: textoDoLimite(cliente?.limite_credito),
    enderecos: Array.isArray(cliente?.enderecos) ? cliente.enderecos : [],
    observacoes: cliente?.observacoes || "",
  };
}

/** Seção com dado já gravado começa aberta: a pessoa vê o que existe. */
function secoesIniciais(valores) {
  return {
    [SECAO_CONTATO]: Boolean(valores.telefone_secundario),
    [SECAO_COMERCIAL]: Boolean(
      valores.representante || valores.marcadores.length || valores.limite_credito
    ),
  };
}

/**
 * Validacao client-side do cadastro (backend revalida).
 *
 * Documento gravado que volta igual (mesmo número e tipo) não é revalidado,
 * como no backend: cliente antigo com DV errado continua editável.
 */
function validarCadastro(valores, cliente) {
  const erros = {};
  if (!valores.nome.trim()) erros.nome = MSG_NOME_OBRIGATORIO;
  const documentoMudou =
    normalizarDocumento(valores.documento) !== normalizarDocumento(cliente?.documento)
    || valores.tipo !== (cliente?.tipo || PESSOA_FISICA);
  const erroDocumento = documentoMudou ? erroDoDocumento(valores.documento, valores.tipo) : "";
  if (erroDocumento) erros.documento = erroDocumento;
  const { erro: erroLimite } = lerLimite(valores.limite_credito);
  if (erroLimite) erros.limite_credito = erroLimite;
  return erros;
}

/** O que vai no POST/PATCH: só os campos do tipo, documento só com o número. */
function montarDados(valores) {
  const camposDoOutroTipo =
    valores.tipo === PESSOA_JURIDICA ? CAMPOS_SO_DE_PESSOA_FISICA : CAMPOS_SO_DE_PESSOA_JURIDICA;
  const dados = Object.fromEntries(
    Object.entries(valores).filter(([campo]) => !camposDoOutroTipo.includes(campo))
  );
  return {
    ...dados,
    ...(valores.tipo === PESSOA_FISICA
      // DateField do backend aceita null, mas rejeita string vazia.
      ? { data_nascimento: valores.data_nascimento || null }
      : {}),
    documento: normalizarDocumento(valores.documento),
    representante: valores.representante || null,
    limite_credito: lerLimite(valores.limite_credito).valor,
    // Cartão adicionado e deixado em branco fica de fora (mesma regra do lead).
    enderecos: prepararEnderecos(valores.enderecos, { permitirCobranca: true }).enderecos,
  };
}

/**
 * Erros do 400 nos campos do cadastro.
 *
 * Dois formatos chegam: o de serializer (`{erros: {campo: msg}}`, lido por
 * `errosPorCampo`) e o de endereço recusado, em que os outros campos vêm no
 * topo do corpo, em lista (`{documento: [msg], detalhe, enderecos}`). Os
 * erros de endereço vão aos cartões por `errosDaRespostaNosCartoes`.
 */
function errosDaResposta(err) {
  const payload = err?.payload && typeof err.payload === "object" ? err.payload : {};
  const erros = errosPorCampo(err);
  if (payload.enderecos !== undefined) {
    Object.entries(payload)
      .filter(([chave]) => !CHAVES_QUE_NAO_SAO_CAMPO.includes(chave))
      .forEach(([campo, mensagem]) => {
        erros[campo] = Array.isArray(mensagem) ? mensagem.join(" ") : String(mensagem);
      });
  }
  const semCampoNaTela = Object.keys(erros).filter(
    (campo) => campo !== CAMPO_GERAL && !CAMPOS_NA_TELA.includes(campo)
  );
  if (!semCampoNaTela.length) return erros;
  const naTela = Object.fromEntries(
    Object.entries(erros).filter(([campo]) => !semCampoNaTela.includes(campo))
  );
  naTela[CAMPO_GERAL] = [erros[CAMPO_GERAL], ...semCampoNaTela.map((campo) => erros[campo])]
    .filter(Boolean)
    .join(" ");
  return naTela;
}

function secoesComErro(erros) {
  return Object.fromEntries(
    Object.entries(CAMPOS_DA_SECAO).map(([secao, campos]) => [
      secao,
      campos.some((campo) => erros[campo]),
    ])
  );
}

function ErroDoCampo({ campo, erros }) {
  if (!erros[campo]) return null;
  return (
    <InlineHint id={`cliente-${campo}-erro`} style={{ color: "var(--danger-text)" }}>
      {erros[campo]}
    </InlineHint>
  );
}

function SecaoRecolhivel({ id, titulo, resumo, aberta, onAlternar, children }) {
  return (
    <div className="full" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          aria-expanded={aberta}
          aria-controls={aberta ? id : undefined}
          onClick={onAlternar}
        >
          {aberta ? <FiChevronDown aria-hidden="true" /> : <FiChevronRight aria-hidden="true" />}
          {titulo}
        </button>
        {!aberta && <InlineHint>{resumo}</InlineHint>}
      </div>
      {aberta && <FormGrid id={id}>{children}</FormGrid>}
    </div>
  );
}

function ClienteForm({
  cliente,
  onSubmit,
  onCancel,
  loading,
  buscarCep,
  carregarParceiros,
  consultarContatoExistente,
}) {
  // CRM-12: aviso de contato ja cadastrado no blur (telefone/e-mail so
  // avisam; CPF/CNPJ repetido o backend recusa no campo).
  const contatoExistente = useContatoExistente(consultarContatoExistente);
  const verificarContato = (parcial) =>
    contatoExistente.verificar({
      email: valores.email,
      telefone: valores.telefone,
      documento: valores.documento,
      ...parcial,
      excluir_cliente: cliente?.id,
    });
  const [valores, setValores] = useState(() => valoresIniciais(cliente));
  const [erros, setErros] = useState({});
  const [errosEnderecos, setErrosEnderecos] = useState([]);
  const [revalidarEnderecos, setRevalidarEnderecos] = useState(false);
  const [secoesAbertas, setSecoesAbertas] = useState(() => secoesIniciais(valoresIniciais(cliente)));
  const [parceiros, setParceiros] = useState([]);
  const [avisoParceiros, setAvisoParceiros] = useState("");

  useEffect(() => {
    const iniciais = valoresIniciais(cliente);
    setValores(iniciais);
    setErros({});
    setErrosEnderecos([]);
    setRevalidarEnderecos(false);
    setSecoesAbertas(secoesIniciais(iniciais));
  }, [cliente]);

  useEffect(() => {
    if (!carregarParceiros) return undefined;
    let vivo = true;
    // `Promise.resolve().then(...)`: a falha de quem carrega (inclusive erro
    // sincrono) cai no catch, em vez de derrubar a renderizacao do form.
    Promise.resolve()
      .then(() => carregarParceiros())
      .then((pagina) => {
        if (!vivo) return;
        const lista = Array.isArray(pagina) ? pagina : pagina?.results || [];
        setParceiros(lista.filter((parceiro) => parceiro?.ativo !== false));
      })
      .catch(() => {
        if (vivo) setAvisoParceiros("Não foi possível carregar os parceiros. O indicado atual foi mantido.");
      });
    return () => {
      vivo = false;
    };
  }, [carregarParceiros]);

  // Só parceiros ativos da unidade; o indicado atual, mesmo aposentado,
  // continua como opção para a edição não apagá-lo sem ninguém pedir.
  const opcoesDeParceiro = useMemo(() => {
    const atual = cliente?.representante_resumo;
    if (!atual?.id || parceiros.some((p) => String(p.id) === String(atual.id))) return parceiros;
    return [...parceiros, { ...atual, nome: `${atual.nome} (inativo)` }];
  }, [parceiros, cliente]);

  // `originais` = enderecos como vieram do servidor: legado inalterado (ex.:
  // UF "XX" gravada antes da validacao) nao trava a edicao do resto.
  const opcoesDosEnderecos = {
    permitirCobranca: true,
    originais: cliente?.enderecos || [],
  };

  const setCampo = (campo, valor) =>
    setValores((prev) => ({ ...prev, [campo]: valor }));

  const atributosDoCampo = (campo) => ({
    id: `cliente-${campo}`,
    "aria-invalid": Boolean(erros[campo]),
    "aria-describedby": erros[campo] ? `cliente-${campo}-erro` : undefined,
  });

  const alternarSecao = (secao) =>
    setSecoesAbertas((prev) => ({ ...prev, [secao]: !prev[secao] }));

  const mostrarErros = (novosErros) => {
    setErros(novosErros);
    const comErro = secoesComErro(novosErros);
    setSecoesAbertas((prev) => ({
      [SECAO_CONTATO]: prev[SECAO_CONTATO] || comErro[SECAO_CONTATO],
      [SECAO_COMERCIAL]: prev[SECAO_COMERCIAL] || comErro[SECAO_COMERCIAL],
    }));
  };

  const alterarEnderecos = (lista) => {
    setValores((prev) => ({ ...prev, enderecos: lista }));
    // Depois da primeira tentativa de salvar, os erros acompanham a lista:
    // somem quando a pessoa corrige e seguem o cartao quando ela reordena
    // (os erros sao por posicao).
    if (revalidarEnderecos) setErrosEnderecos(validarCartoes(lista, opcoesDosEnderecos));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const errosDoCadastro = validarCadastro(valores, cliente);
    const errosDosEnderecos = validarCartoes(valores.enderecos, opcoesDosEnderecos);
    mostrarErros(errosDoCadastro);
    setErrosEnderecos(errosDosEnderecos);
    setRevalidarEnderecos(true);
    if (Object.keys(errosDoCadastro).length || errosDosEnderecos.length) return;

    try {
      await onSubmit(montarDados(valores));
    } catch (err) {
      mostrarErros(errosDaResposta(err));
      setErrosEnderecos(errosDaRespostaNosCartoes(err, valores.enderecos) || []);
    }
  };

  const pj = valores.tipo === PESSOA_JURIDICA;

  return (
    <Form onSubmit={handleSubmit}>
      {erros[CAMPO_GERAL] && <ErrorMsg role="alert">{erros[CAMPO_GERAL]}</ErrorMsg>}

      <FormGrid>
        <Field as="div">
          <label htmlFor="cliente-tipo">Tipo</label>
          <FormSelect
            {...atributosDoCampo("tipo")}
            value={valores.tipo}
            onChange={(e) => setCampo("tipo", e.target.value)}
            // CRM-14 (decisao do dono): a troca PF/PJ e livre ate existir
            // NF-e autorizada ou contrato assinado — o backend diz qual.
            disabled={Boolean(cliente?.tipo_travado_por)}
            aria-label="Tipo de cliente"
          >
            {TIPOS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </FormSelect>
          {cliente?.tipo_travado_por && (
            <InlineHint>
              Tipo não pode mais ser alterado: este cliente já tem{" "}
              {cliente.tipo_travado_por}.
            </InlineHint>
          )}
          <ErroDoCampo campo="tipo" erros={erros} />
        </Field>

        <Field as="div">
          <label htmlFor="cliente-perfil">Perfil</label>
          <FormSelect
            {...atributosDoCampo("perfil")}
            value={valores.perfil}
            onChange={(e) => setCampo("perfil", e.target.value)}
          >
            {PERFIS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </FormSelect>
          <ErroDoCampo campo="perfil" erros={erros} />
        </Field>

        <Field as="div" className="full">
          <label htmlFor="cliente-nome">Nome {pj ? "(razão social)" : "completo"} *</label>
          <Input
            {...atributosDoCampo("nome")}
            type="text"
            value={valores.nome}
            onChange={(e) => setCampo("nome", e.target.value)}
            autoFocus
            required
          />
          <ErroDoCampo campo="nome" erros={erros} />
        </Field>

        {pj && (
          <>
            <Field as="div">
              <label htmlFor="cliente-nome_fantasia">Nome fantasia</label>
              <Input
                {...atributosDoCampo("nome_fantasia")}
                type="text"
                value={valores.nome_fantasia}
                onChange={(e) => setCampo("nome_fantasia", e.target.value)}
              />
              <ErroDoCampo campo="nome_fantasia" erros={erros} />
            </Field>

            <Field as="div">
              <label htmlFor="cliente-inscricao_estadual">Inscrição estadual</label>
              <Input
                {...atributosDoCampo("inscricao_estadual")}
                type="text"
                value={valores.inscricao_estadual}
                onChange={(e) => setCampo("inscricao_estadual", e.target.value)}
                maxLength={20}
                placeholder="Ou ISENTO"
              />
              <ErroDoCampo campo="inscricao_estadual" erros={erros} />
            </Field>
          </>
        )}

        <Field as="div">
          <label htmlFor="cliente-documento">{pj ? "CNPJ" : "CPF"}</label>
          <Input
            {...atributosDoCampo("documento")}
            type="text"
            value={valores.documento}
            onChange={(e) => setCampo("documento", e.target.value)}
            onBlur={(e) => {
              setCampo("documento", mascaraDocumento(e.target.value, valores.tipo));
              verificarContato({ documento: e.target.value });
            }}
            placeholder={pj ? "00.000.000/0000-00" : "000.000.000-00"}
          />
          <ErroDoCampo campo="documento" erros={erros} />
        </Field>

        <Field as="div">
          <label htmlFor="cliente-telefone">Telefone</label>
          <Input
            {...atributosDoCampo("telefone")}
            type="tel"
            value={valores.telefone}
            onChange={(e) => setCampo("telefone", e.target.value)}
            onBlur={(e) => {
              setCampo("telefone", mascaraTelefone(e.target.value));
              verificarContato({ telefone: e.target.value });
            }}
            placeholder="(11) 99999-9999"
          />
          <ErroDoCampo campo="telefone" erros={erros} />
        </Field>

        <Field as="div" className="full">
          <label htmlFor="cliente-email">Email</label>
          <Input
            {...atributosDoCampo("email")}
            type="email"
            value={valores.email}
            onChange={(e) => setCampo("email", e.target.value)}
            onBlur={(e) => verificarContato({ email: e.target.value })}
            placeholder="cliente@exemplo.com"
          />
          <ErroDoCampo campo="email" erros={erros} />
        </Field>
        <Field as="div" className="full">
          <AvisoContatoExistente candidatos={contatoExistente.candidatos} />
        </Field>

        {!pj && (
          <>
            <Field as="div">
              <label htmlFor="cliente-rg">RG</label>
              <Input
                {...atributosDoCampo("rg")}
                type="text"
                value={valores.rg}
                onChange={(e) => setCampo("rg", e.target.value)}
                maxLength={20}
                placeholder="00.000.000-0"
              />
              <ErroDoCampo campo="rg" erros={erros} />
            </Field>

            <Field as="div">
              <label htmlFor="cliente-data_nascimento">Data de nascimento</label>
              <Input
                {...atributosDoCampo("data_nascimento")}
                type="date"
                value={valores.data_nascimento}
                onChange={(e) => setCampo("data_nascimento", e.target.value)}
              />
              <ErroDoCampo campo="data_nascimento" erros={erros} />
            </Field>

            <Field as="div">
              <label htmlFor="cliente-estado_civil">Estado civil</label>
              {/* Texto livre: o backend nao tem choices (CharField livre,
                  ex.: "Casada" ja gravado em producao). */}
              <Input
                {...atributosDoCampo("estado_civil")}
                type="text"
                value={valores.estado_civil}
                onChange={(e) => setCampo("estado_civil", e.target.value)}
                maxLength={20}
                placeholder="Ex.: Casada, Solteiro"
              />
              <ErroDoCampo campo="estado_civil" erros={erros} />
            </Field>

            <Field as="div">
              <label htmlFor="cliente-profissao">Profissão</label>
              <Input
                {...atributosDoCampo("profissao")}
                type="text"
                value={valores.profissao}
                onChange={(e) => setCampo("profissao", e.target.value)}
                maxLength={80}
                placeholder="Ex.: Arquiteta"
              />
              <ErroDoCampo campo="profissao" erros={erros} />
            </Field>
          </>
        )}

        <SecaoRecolhivel
          id="cliente-secao-contato"
          titulo="Contato adicional"
          resumo="Telefone 2"
          aberta={secoesAbertas[SECAO_CONTATO]}
          onAlternar={() => alternarSecao(SECAO_CONTATO)}
        >
          <Field as="div">
            <label htmlFor="cliente-telefone_secundario">Telefone 2</label>
            <Input
              {...atributosDoCampo("telefone_secundario")}
              type="tel"
              value={valores.telefone_secundario}
              onChange={(e) => setCampo("telefone_secundario", e.target.value)}
              onBlur={(e) => setCampo("telefone_secundario", mascaraTelefone(e.target.value))}
              // 30, igual a Cliente.telefone_secundario e ao Lead (CRM-03).
              maxLength={30}
              placeholder="(11) 99999-9999"
            />
            <ErroDoCampo campo="telefone_secundario" erros={erros} />
          </Field>
        </SecaoRecolhivel>

        <SecaoRecolhivel
          id="cliente-secao-comercial"
          titulo="Comercial"
          resumo="Parceiro indicador, marcadores e limite de crédito"
          aberta={secoesAbertas[SECAO_COMERCIAL]}
          onAlternar={() => alternarSecao(SECAO_COMERCIAL)}
        >
          <Field as="div" className="full">
            <label htmlFor="cliente-representante">Indicado por (parceiro)</label>
            <FormSelect
              {...atributosDoCampo("representante")}
              value={valores.representante}
              onChange={(e) => setCampo("representante", e.target.value)}
            >
              <option value="">— Nenhum —</option>
              {opcoesDeParceiro.map((parceiro) => (
                <option key={parceiro.id} value={parceiro.id}>
                  {parceiro.nome}
                </option>
              ))}
            </FormSelect>
            {avisoParceiros && <InlineHint>{avisoParceiros}</InlineHint>}
            <ErroDoCampo campo="representante" erros={erros} />
          </Field>

          <Field as="div" className="full" role="group" aria-labelledby="cliente-marcadores-rotulo">
            <span id="cliente-marcadores-rotulo">Marcadores</span>
            <TagsInput
              value={valores.marcadores}
              onChange={(lista) => setCampo("marcadores", lista)}
              idPrefix="cliente-marcadores"
              disabled={loading}
            />
            <ErroDoCampo campo="marcadores" erros={erros} />
          </Field>

          <Field as="div">
            <label htmlFor="cliente-limite_credito">Limite de crédito</label>
            <Input
              {...atributosDoCampo("limite_credito")}
              type="text"
              inputMode="decimal"
              value={valores.limite_credito}
              onChange={(e) => setCampo("limite_credito", e.target.value)}
              onBlur={(e) => {
                // Formata no blur, nunca a cada tecla (ADR-0059). Negativo ou
                // ilegível fica como digitado para o erro apontar o que corrigir.
                const numero = parseDinheiro(e.target.value);
                if (numero !== null && numero >= 0) setCampo("limite_credito", formatarDinheiro(numero));
              }}
              placeholder="Ex.: 15.000,00"
            />
            <InlineHint>Vazio = sem limite.</InlineHint>
            <ErroDoCampo campo="limite_credito" erros={erros} />
          </Field>
        </SecaoRecolhivel>

        <FieldGroup className="full">
          <FieldGroupTitle>Endereços</FieldGroupTitle>
          <InlineHint>
            O primeiro é o principal. Digite o CEP para preencher rua, bairro, cidade e UF.
          </InlineHint>
          <EnderecosEditor
            value={valores.enderecos}
            onChange={alterarEnderecos}
            buscar={buscarCep}
            permitirCobranca
            disabled={loading}
            erros={errosEnderecos}
            idPrefix="cliente-enderecos"
          />
        </FieldGroup>

        <Field as="div" className="full">
          <label htmlFor="cliente-observacoes">Observações</label>
          <Textarea
            {...atributosDoCampo("observacoes")}
            value={valores.observacoes}
            onChange={(e) => setCampo("observacoes", e.target.value)}
            rows={3}
          />
          <ErroDoCampo campo="observacoes" erros={erros} />
        </Field>
      </FormGrid>

      <FormFooter>
        <SecondaryButton type="button" onClick={onCancel} disabled={loading}>
          Cancelar
        </SecondaryButton>
        <PrimaryButton type="submit" disabled={loading}>
          {loading ? "Salvando..." : cliente?.id ? "Salvar alterações" : "Criar cliente"}
        </PrimaryButton>
      </FormFooter>
    </Form>
  );
}

export default ClienteForm;
